/// <reference path="../_references.ts"/>

module App.Administrative.VendorInventory {
    const PAGE_SIZE = 100;

    import Warehouse = App.Administrative.Inventory.Warehouse;

    type FilterMapSection = {
        [id: number]: boolean;
    }

    type FilterMap = {
        tags: {[groupId: number]: FilterMapSection};
    }

    interface FilterDataBase<Type extends FilterType> {
        name: string;
        id: string;
        type: Type;
    }
    interface TagFilterData extends FilterDataBase<FilterType.Tag> {
        group: number;
        tag: number;
    }

    type FilterData = FilterDataBase<any>;

    enum FilterType {
        Tag,
    }

    export class VendorInventoryController {
        protected user: App.User.User;
        protected warehouses: Warehouse[];
        protected tagGroups: TagGroup[];

        protected filterMap: FilterMap = {
            tags: {},
        };
        protected filterCollapseMap = {
            tags: {},
        };
        protected filterFavouries: boolean = false;
        protected inventorySelectionMap: { [warehouseId: number]: { [invId: number]: number } } = {};
        protected inventorySerializedMap: { [warehouseId: number]: { [invId: number]: number[] } } = {};

        public inventories: VendorInventory[] = [];
        protected savedInventoryData: {[id: number]: VendorInventory} = {};

        public warehouse: Warehouse;
        public appointment: App.Appointments.Appointment;

        public searchText: string = '';
        public page: number = 1;
        public maxPage: number = 1;

        public isLoading: boolean = true;
        public isSearching: boolean = false;
        public isSaving: boolean = false;

        public searchCollapsed: boolean = false;
        public selectedCollapsed: boolean = false;
        public expandCollapseToggle: boolean = false;

        static $inject: string[] = [
            '$q',
            'InventoryService', 'OfficeService', 'SelectedAppointment', 'TitleBarService', 'ToastService',
            'UserSession', 'VendorInventoryService',
        ];
        constructor(
            protected $q: ng.IQService,
            protected InventoryService: App.Administrative.Inventory.InventoryService,
            protected OfficeService: App.Utils.OfficeService,
            protected SelectedAppointment: App.Appointments.SelectedAppointment,
            protected TitleBarService: App.Helpers.TitleBarService,
            protected ToastService: App.Utils.ToastService,
            protected UserSession: App.User.UserSession,
            protected VendorInventoryService: VendorInventoryService
        ) {
            TitleBarService.set('Appointment');
            this.$q.all([
                this.UserSession.getUser()
                    .then(user => {
                        this.user = user;
                        return this.InventoryService.getWarehouses()
                            .then((response) => {
                                this.warehouses = response.data;
                                this.warehouse = this.warehouses.find(x => {
                                    return x.user.id === this.user.id;
                                });
                            });
                    })
                    .catch(err => ToastService.showError('Failed to load user information: ', err)),

                this.getAppointment()
                    .then(appt => {
                        this.appointment = appt;
                    })
                    .catch(err => ToastService.showError('Failed to load appointment data: ', err)),

                this.VendorInventoryService.getTagGroups({ showTags: 1, 'neq@tag': 'null' })
                    .then(r => this.tagGroups = r.data)
                    .catch(err => ToastService.showError('Failed to load filters: ', err)),
            ]).finally(() => this.isLoading = false);
        }

        protected filterCache: {[id: string]: FilterData} = {};
        public getActiveFilters(): FilterData[] {
            if (this.isLoading) {
                return [];
            }

            const filters: FilterData[] = [];

            for (let group in this.filterMap.tags) {
                Object.keys(this.filterMap.tags[group])
                    .filter(k => this.filterMap.tags[group][k])
                    .forEach(tId => {
                        const id = 't' + tId;
                        if (!this.filterCache[id]) {
                            const tagGroup = this.tagGroups.find(g => g.id === Number(group));

                            this.filterCache[id] = <TagFilterData>{
                                id: id,
                                name: tagGroup.name + ': ' + tagGroup.tags.find(t => t.id === Number(tId)).name,
                                type: FilterType.Tag,
                                group: Number(group),
                                tag: Number(tId),
                            };
                        }

                        filters.push(this.filterCache[id]);
                    });
            }

            return filters;
        }
        public removeFilter(filter: FilterData) {
            switch (filter.type) {
                case FilterType.Tag:
                    this.filterMap.tags[(<TagFilterData>filter).group][(<TagFilterData>filter).tag] = false;
                    break;
            }
        }

        protected search(page: number = 1) {
            if (this.isSearching) {
                return;
            }
            this.isSearching = true;
            const params = {
                quiet: 1,
                show_full_counts: 1,
                size: PAGE_SIZE,
                page: page,
                query: this.searchText,
                orderBy: 'name',
                sort: 'ASC',
                show_favourite: 1,
            };

            if (this.warehouse) {
                params['warehouses[]'] = this.warehouse.id;
            }
            if (this.filterFavouries) {
            	params['favourite'] = 'current';
            }

            for (let group in this.filterMap.tags) {
                const tags = Object.keys(this.filterMap.tags[group])
                    .filter(k => this.filterMap.tags[group][k]);

                if (tags.length) {
                    params['AND_' + group + '[tags][]'] = tags;
                }
            }

            this.VendorInventoryService.getAll(params)
                .then(r => {
                    this.inventories = r.data
                        .map(i => {
                            const counts: WarehouseCountMap = {};
                            (<WarehouseCount[]>i.warehouse_counts).forEach(c => {
                                counts[c.warehouse.id] = c;
                            });
                            this.warehouses.forEach(w => {
                                if (!counts[w.id]) {
                                    counts[w.id] = {
                                        id: null,
                                        count: 0,
                                        warehouse: w,
                                        serialized_items: [],
                                    };
                                }
                            });
                            i.warehouse_counts = counts;

                            return i;
                        });
                    this.maxPage = Math.ceil(Number(r.headers('count')) / PAGE_SIZE);
                })
                .catch(err => this.ToastService.showError('Failed to load inventory: ', err))
                .finally(() => this.isSearching = false)
        }

        protected getAppointment(): ng.IPromise<App.Appointments.Appointment|null> {
            const defer = this.$q.defer<App.Appointments.Appointment|null>();

            this.OfficeService.getAppointment()
                .then(r => {
                    defer.resolve(r.data);
                })
                .catch(() => {
                    this.SelectedAppointment.getAppointment()
                        .then(appt => {
                            defer.resolve(appt)
                        }).catch((response) => {
                            if (response === this.SelectedAppointment.NO_APPOINTMENT) {
                                defer.resolve(null);
                            } else {
                                defer.reject(response);
                            }
                        });
                });

            return defer.promise;
        }


        public hasFilters(): boolean {
            for (let group in this.filterMap.tags) {
                for (let id in this.filterMap.tags[group]) {
                    if (this.filterMap.tags[group][id]) {
                        return true;
                    }
                }
            }

            return false;
        }

        public clearFilters(): void {
            for (let group in this.filterMap.tags) {
                this.filterMap.tags[group] = {};
            }
        }

        public createReplenishment() {
            if (this.isSaving || !this.hasInventory()) {
                return;
            }
            this.isSaving = true;

            const data = [];
            Object.keys(this.inventorySelectionMap).forEach(wId => {
                Object.keys(this.inventorySelectionMap[wId]).forEach(iId => {
                    if (this.inventorySelectionMap[wId][iId] > 0) {
                        data.push({
                            warehouse: wId,
                            inventory: iId,
                            count: this.inventorySelectionMap[wId][iId],
                            appointment: this.appointment && this.appointment.id || null,
                            serialized_items: this.inventorySerializedMap[wId] && this.inventorySerializedMap[wId][iId] || null,
                        });
                    }
                });
            });

            this.VendorInventoryService.createReplenishments(data, { quiet: 1 })
                .then(r => {
                    this.ToastService.show('Successfully created replenishment request');
                    // TODO: Redirect out
                    Object.keys(this.savedInventoryData).forEach(iId => {
                        const inv = this.savedInventoryData[iId];
                        this.warehouses.forEach(w => {
                            this.removeInventory(w, inv);
                        });
                        delete this.savedInventoryData[iId];
                    })
                })
                .catch(err => this.ToastService.showError('Failed to create replenishment: ', err))
                .finally(() => this.isSaving = false);
        }

        public hasInventory(warehouse: null|Warehouse = null): boolean {
            const warehouses = warehouse
                ? [warehouse]
                : this.warehouses;
            for (let w of warehouses) {
                for (let invId in this.inventorySelectionMap[w.id]) {
                    if (this.inventorySelectionMap[w.id][invId] > 0) {
                        return true;
                    }
                }
            }
            return false;
        }
        public getInventory(warehouse: Warehouse): VendorInventory[] {
            return Object.keys(this.inventorySelectionMap[warehouse.id])
                .map(id => this.savedInventoryData[id]);
        }
        public handleInventoryChange(warehouse: Warehouse, inventory: VendorInventory) {
            if (this.inventorySelectionMap[warehouse.id][inventory.id] <= 0) {
                this.removeInventory(warehouse, inventory);
            } else {
                this.savedInventoryData[inventory.id] = inventory;
                if (inventory.serialized) {
                    this.inventorySelectionMap[warehouse.id][inventory.id] = Math.min(
                        this.inventorySelectionMap[warehouse.id][inventory.id],
                        inventory.warehouse_counts[warehouse.id].count
                    );

                    this.inventorySerializedMap[warehouse.id] =
                        this.inventorySerializedMap[warehouse.id] || {};
                    this.inventorySerializedMap[warehouse.id][inventory.id] =
                        this.inventorySerializedMap[warehouse.id][inventory.id] || [];
                    while (this.inventorySerializedMap[warehouse.id][inventory.id].length < this.inventorySelectionMap[warehouse.id][inventory.id]) {
                        this.inventorySerializedMap[warehouse.id][inventory.id].push(null);
                    }
                    while (this.inventorySerializedMap[warehouse.id][inventory.id].length > this.inventorySelectionMap[warehouse.id][inventory.id]) {
                        this.inventorySerializedMap[warehouse.id][inventory.id].pop();
                    }
                }
            }
        }
        public removeInventory(warehouse: Warehouse, inventory: VendorInventory) {
            if (this.inventorySelectionMap[warehouse.id]) {
                delete this.inventorySelectionMap[warehouse.id][inventory.id];
            }
            if (this.inventorySerializedMap[warehouse.id]) {
                delete this.inventorySerializedMap[warehouse.id][inventory.id];
            }
        }
        public getUnusedSerializedItems(warehouse: Warehouse, inventory: VendorInventory, index: number): SerializedItem[] {
            return inventory.warehouse_counts[warehouse.id]
                .serialized_items
                .filter(i =>
                    this.inventorySerializedMap[warehouse.id][inventory.id].indexOf(i.id) === -1
                        || this.inventorySerializedMap[warehouse.id][inventory.id][index] === i.id
                );
        }
        public warnNoSerialized(inv: VendorInventory) {
            this.ToastService.show(
                'There are no ' + inv.name + ' serialized items in the warehouse ' + (
                    this.warehouse
                        ? this.warehouse.name
                        : 'selected'
                )
            );
        }
        public expandCollapseAll(): void {
        	this.expandCollapseToggle = !this.expandCollapseToggle;

        	for (const inv of this.inventories) {
        		inv.expanded = this.expandCollapseToggle;
        	}
        }
        public favoriteVendorInv(inv: VendorInventory): void {
        	if (inv.updating) {
        		return;
        	}
        	inv.updating = true;
        	this.VendorInventoryService.setFavourite(inv.id, !inv.favourite, { quiet: 1, show_favourite: 1 })
        		.then((r) => inv.favourite = r.data.favourite)
        		.catch(err => this.ToastService.showError('Failed to mark favourite: ', err))
        		.finally(() => inv.updating = false);
        }
    }
}